
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
// Removed useToast as it's not used here directly

// This interface should align with the relevant parts of get_user_credits RPC output
export interface UserCreditsData {
  free_listings_remaining: number;
  free_listings_used: number;
  purchased_listings_credits: number;
  total_listings_used?: number;
  default_free_listings?: number; // Added from RPC
}

export const useSubscription = () => {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const { data: userCreditsData, isLoading: isLoadingCredits, refetch } = useQuery({
    queryKey: ['userCreditsRpcData', profile?.id], // Using a distinct query key
    queryFn: async (): Promise<UserCreditsData | null> => {
      if (!profile?.id) {
        return null;
      }
      
      try {
        const { data: rpcResult, error: rpcError } = await supabase.rpc(
          'get_user_credits', 
          { p_user_id: profile.id }
        );
        
        if (rpcError) {
          console.error("Error fetching user credits in useSubscription (RPC):", rpcError);
          // Fallback to a default structure on error.
          // The RPC itself provides defaults, so this path indicates a more severe issue.
           return {
              free_listings_remaining: 0,
              free_listings_used: 0,
              purchased_listings_credits: 0,
              total_listings_used: 0,
              default_free_listings: 0, // Fallback default
            } as UserCreditsData;
        }
        
        const rpcCredits = rpcResult?.[0];

        if (!rpcCredits) {
            // This case should ideally not happen if the RPC always returns data (even defaults)
            console.warn("useSubscription: RPC get_user_credits returned no data for user", profile.id);
            return {
              free_listings_remaining: 0, 
              free_listings_used: 0,
              purchased_listings_credits: 0,
              total_listings_used: 0,
              default_free_listings: 0, // Fallback default
            } as UserCreditsData;
        }
        
        const credits: UserCreditsData = {
          free_listings_remaining: rpcCredits.free_listings_remaining ?? 0,
          free_listings_used: rpcCredits.free_listings_used ?? 0,
          purchased_listings_credits: rpcCredits.purchased_listings_credits ?? 0,
          total_listings_used: rpcCredits.total_listings_used ?? 0,
          default_free_listings: rpcCredits.default_free_listings ?? 0,
        };
        
        return credits;

      } catch (error) {
        console.error("Error fetching user credits in useSubscription (Catch):", error);
        return { // Default fallback
          free_listings_remaining: 0, 
          free_listings_used: 0,
          purchased_listings_credits: 0,
          total_listings_used: 0,
          default_free_listings: 0,
        } as UserCreditsData;
      }
    },
    enabled: !!profile?.id,
  });

  const handleBuyCredits = () => {
    navigate('/coins');
  };

  return {
    profile,
    userCredits: userCreditsData,
    isLoadingCredits,
    handleBuyCredits,
    refetchCredits: refetch
  };
};

export default useSubscription;
